let sourceEntries;
let parsedEntries = [];

/** Returns whether the URL's origin is a configured Sparkle API host. */
export function isSparkleDomain(url = window.location) {
  try {
    const origin = new URL(url).origin;

    return embeddingDomains().some(({ apiHost }) => apiHost === origin);
  } catch {
    return false;
  }
}

/** Returns the URL's configured publishing domain; undefined when unmatched and null for an invalid URL. */
export function detectPublishingDomain(url = window.location) {
  return lookup(url, "publishingDomain");
}

/** Returns the URL's configured website; undefined when unmatched and null for an invalid URL. */
export function detectCurrentWebsite(url = window.location) {
  return lookup(url, "website");
}

/**
 * Returns the URL's configured website, or `fallbackWebsite` when no embedding endpoint matches.
 * Returns `undefined` for an endpoint without a website and `null` for an invalid URL.
 */
export function detectCurrentWebsiteWithFallback(fallbackWebsite, url = window.location) {
  const website = detectCurrentWebsite(url);

  if (website !== undefined) {
    return website;
  }

  if (detectEmbeddingEndpoint(url) !== undefined) {
    return undefined;
  }

  return fallbackWebsite;
}

/** Returns the URL's configured embedding endpoint; undefined when unmatched and null for an invalid URL. */
export function detectEmbeddingEndpoint(url = window.location) {
  return lookup(url, "embeddingEndpoint");
}

/** Returns the URL's ordered URL mappings, empty for an unmatched or invalid URL. */
export function detectURLMap(url = window.location) {
  return new Map(lookup(url, "urlMap"));
}

/**
 * Returns how the host page at the URL takes a locale on its own URLs, "param" or "path"
 * (the endpoint's `url_locale` in publishing_domains.yml); "param" for an unmatched or invalid URL.
 */
export function detectEmbeddingUrlLocale(url = window.location) {
  return lookup(url, "embeddingUrlLocale") || "param";
}

function lookup(url, property) {
  try {
    const incoming = new URL(url);
    const match = embeddingDomains().find(({ rootUrl }) => matchUrl(rootUrl, incoming));

    return match?.[property];
  } catch (error) {
    console.error(error);

    return null;
  }
}

function embeddingDomains() {
  const entries = window.SparkleConfig?.embedPublishingDomains;

  if (entries !== sourceEntries) {
    parsedEntries = (entries ?? []).map((entry) => ({ ...entry, rootUrl: new URL(entry.rootUrl) }));
    sourceEntries = entries;
  }

  return parsedEntries;
}

// A host page under a locale prefix belongs to the endpoint of its bare path: `/es/find-a-doctor` matches a
// root of `https://host.org/find-a-doctor`.
function matchUrl(rootUrl, incomingUrl) {
  const root = terminatedPathname(rootUrl.pathname);

  return (
    incomingUrl.hostname === rootUrl.hostname &&
    [incomingUrl.pathname, unlocalizedPathname(incomingUrl.pathname)].some((pathname) =>
      terminatedPathname(pathname).startsWith(root),
    )
  );
}

// Returns `pathname` without a leading non-default locale segment, matched exactly as the server matches it:
// "/es/find-a-doctor" → "/find-a-doctor".
function unlocalizedPathname(pathname) {
  const segment = pathname.split("/")[1];
  const available = window.SparkleConfig?.locales?.available ?? [];
  const defaultLocale = window.SparkleConfig?.locales?.default ?? "en";

  if (segment === defaultLocale || !available.includes(segment)) {
    return pathname;
  }

  return pathname.slice(segment.length + 1) || "/";
}

function terminatedPathname(pathname) {
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}
